import React, {createRef, useState, useContext} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import moment from "moment";
import {AddBox, Edit} from "@material-ui/icons";
import MaterialTable from "@material-table/core";
import {TablePagination, Checkbox, Select, MenuItem, IconButton, Typography, useTheme} from "@material-ui/core";

import {
  MaskedText,
  DatePicker,
} from "../../../controls";
import SystemContext from "../../../SystemContext";
import { getApiSitePrefix } from "../../../functions/SiteDefaults";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import PersonsValidator from "../validators/PersonsValidator";
import editPersonDialog from "./EditPersonDialog";
import { usePersons, useAddPerson, useUpdatePerson, useDeletePerson } from "../../../services/persons";

const apiSitePrefix = getApiSitePrefix();
const dateFormat = "DD/MM/YYYY";
const today = moment().toISOString();
const formatChars={
  "U": "[A-Z]",
  "A": "[A-Z0-9]",
  "a": "[A-Za-z0-9]",
  "i": "[A-Za-z0-9\\-]",
  "9": "[0-9]",
  "D": "[0-9.]",
  "-": "-",
  "x": "."
}

const PersonDialogs = (SystemData, apiSitePrefix, extraKeys, handleAdd, handleUpdate, formatChars, classes) => {
  const {Dialog: EditPersonDialog, open: openEditPerson, close: closeEditPerson} = editPersonDialog(SystemData, apiSitePrefix, extraKeys, handleAdd, handleUpdate, formatChars, classes);

  return {
    EditPersonDialog, openEditPerson, closeEditPerson,
  }
}

const PersonList = (_props) => {
  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const validatorColumn = ValidatorColumn(PersonsValidator, true);

  const props = {
    ..._props
  }
  const classes = props.classes;

  const [state, setState] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddPerson = useAddPerson();
  const handleUpdatePerson = useUpdatePerson();
  const handleDeletePerson = useDeletePerson();

  const queryClient = useQueryClient();
  const extraKeys = [];

  const persons = usePersons({tableRef, queryClient, extraKeys});

  const {
    EditPersonDialog, openEditPerson
  } = PersonDialogs(SystemData, apiSitePrefix, extraKeys, handleAddPerson, handleUpdatePerson, formatChars, classes);

  const handleNewPersonAction = async (rowData) => {
    openEditPerson(rowData, null, true);
  }

  const handleUpdatePersonAction = async (rowData) => {
    openEditPerson(rowData, null, false);
  }

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { fontSize:14, padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };

    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      {...validatorColumn},
      {
        ...baseColumn,
        ...ColumnWidth(50,6,undefined,undefined,undefined,undefined,false),
        title: "Title",
        field: "title",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(4)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        title: "First Name",
        field: "forename",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(30)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        title: "Middle Name",
        field: "middleName",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(30)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        title: "Last Name",
        field: "surname",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(30)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        title: "Known As",
        field: "knownAs",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(30)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        title: "Sex",
        field: "sex",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(100)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            forceUpperCase={true}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        title: "Date of Birth",
        field: "doB",
        type: "date",
        emptyValue: today,
        initialEditValue:today,
        editComponent: (props) => (
            <DatePicker
              // minDate={today}
              value={props.value}
              disableToolbar={true}
              autoOk={true}
              format={dateFormat}
              onChange={props.onChange}
            />
        )
      },
      {
        ...baseColumn,
        title: "Phone",
        field: "phone",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(15)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        title: "Email",
        field: "email",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(15)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
    ];
  };

  return (isAuthenticated &&
      <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
        <MaterialTable
            tableRef={tableRef}
            style={{
              border: "none",
              boxShadow: "none",
              borderRadius: 0,
            }}
            options={{
              actionsColumnIndex: -1,
              cellStyle: {
                padding: "8px 2px",
              },
              headerStyle: {
                backgroundColor: theme.palette.tableHeader.backgroundColor,
                color: theme.palette.tableHeader.textColor,
                padding: "2px",
              },
              toolbarButtonAlignment: "right",
              pageSize: 10,
              detailPanelType: "single",
            }}

            actions={[
              {
                hidden: false,
                disabled: false,
                icon: () => (
                  <IconButton>
                    <AddBox/>
                  </IconButton>
                ),
                iconProps: { style: { color: theme.palette.primary.main }},
                tooltip: "New Person",
                isFreeAction: true,
                onClick: (e, rowData) => {
                  handleNewPersonAction(rowData);
                }
              },
              (rowData) => (
                {
                  hidden: false,
                  disabled: !state.canEdit,
                  icon: () => (
                    <IconButton variant="contained" size={"small"} color={"secondary"}>
                      <Edit/>
                    </IconButton>
                  ),
                  tooltip: "Update Person",
                  //iconProps: { style: { color: theme.palette.primary.main }},
                  isFreeAction: false,
                  onClick: (e, rowData) => {
                    handleUpdatePersonAction(rowData);
                  }
                }
              ),
            ]}

            components={{
              Pagination: (props) => (
                  <TablePagination
                      {...props}
                      rowsPerPageOptions={[5, 10, 15]}
                  />
              ),
            }}
            title="Person Records"
            columns={getColumns({canEdit: state.canEdit})}
            data={persons.data}
            editable={{
              isEditable: () => {
                return state.canEdit;
              },
              isDeletable: () => {
                return state.canEdit;
              },
              onRowAdd: undefined,
              onRowUpdate: undefined,
              onRowDelete: (oldData) =>
                  new Promise((resolve, reject) => {
                    handleDeletePerson.mutate({oldRecord: oldData, resolve, reject, extraKeys});
                  }),
            }}
        />
        <EditPersonDialog classes={classes}/>
      </div>
  );

}

export default PersonList;
